<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4 sm:p-6">
    <div class="w-full max-w-md">
      <!-- Logo -->
      <div class="text-center mb-6 sm:mb-8">
        <div class="flex justify-center mb-4">
          <img src="/logo.png" alt="Logo" class="h-16 sm:h-20 w-auto object-contain" />
        </div>
      </div>

      <div class="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8">
        <div class="flex justify-center mb-4">
          <div class="p-3 bg-[#22282F]/10 rounded-xl">
            <ShieldCheck class="w-7 h-7 text-[#22282F]" />
          </div>
        </div>

        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-1 text-center">
          Configurer la double authentification
        </h2>
        <p class="text-gray-500 text-sm text-center mb-6">
          Pour sécuriser votre accès, activez la double authentification avant de continuer.
        </p>

        <!-- Erreur -->
        <div
          v-if="error"
          class="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2"
        >
          <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p class="text-sm text-red-800">{{ error }}</p>
        </div>

        <!-- Chargement du QR -->
        <div v-if="loadingQr" class="flex flex-col items-center justify-center py-10">
          <div class="w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mb-3" />
          <p class="text-gray-500 text-sm">Génération du QR code…</p>
        </div>

        <template v-else>
          <!-- Étapes -->
          <ol class="space-y-4 mb-6">
            <li class="flex gap-3">
              <span class="flex-shrink-0 w-6 h-6 rounded-full bg-[#22282F] text-white text-xs font-semibold flex items-center justify-center">1</span>
              <p class="text-sm text-gray-700">
                Installez une application d'authentification (Google Authenticator, Authy, Microsoft Authenticator…).
              </p>
            </li>
            <li class="flex gap-3">
              <span class="flex-shrink-0 w-6 h-6 rounded-full bg-[#22282F] text-white text-xs font-semibold flex items-center justify-center">2</span>
              <div class="flex-1">
                <p class="text-sm text-gray-700 mb-3">Scannez ce QR code :</p>
                <div class="flex justify-center">
                  <div class="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <img v-if="qrCode" :src="qrCode" alt="QR code 2FA" class="w-44 h-44" />
                  </div>
                </div>
                <!-- Clé manuelle -->
                <div v-if="secret" class="mt-3">
                  <button
                    type="button"
                    @click="showSecret = !showSecret"
                    class="text-xs text-[#22282F] hover:underline flex items-center gap-1 mx-auto"
                  >
                    <KeyRound class="w-3.5 h-3.5" />
                    {{ showSecret ? 'Masquer' : 'Saisie manuelle impossible ? Afficher la clé' }}
                  </button>
                  <div
                    v-if="showSecret"
                    class="mt-2 flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                  >
                    <code class="text-xs text-gray-800 break-all">{{ secret }}</code>
                    <button type="button" @click="copySecret" class="text-gray-400 hover:text-[#22282F]" title="Copier">
                      <Copy class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </li>
            <li class="flex gap-3">
              <span class="flex-shrink-0 w-6 h-6 rounded-full bg-[#22282F] text-white text-xs font-semibold flex items-center justify-center">3</span>
              <p class="text-sm text-gray-700">Saisissez le code à 6 chiffres affiché par l'application :</p>
            </li>
          </ol>

          <!-- Régénérer le QR code (si l'utilisateur a déjà scanné un ancien code) -->
          <div class="flex justify-center mb-2">
            <button
              type="button"
              @click="regenerateQr"
              :disabled="isLoading || loadingQr"
              class="text-xs text-gray-400 hover:text-[#22282F] underline transition-colors disabled:opacity-40"
            >
              Le code ne fonctionne pas ? Générer un nouveau QR code
            </button>
          </div>

          <!-- Saisie du code -->
          <form @submit.prevent="confirm" class="space-y-5">
            <div class="flex items-center justify-center gap-2 sm:gap-3">
              <input
                v-for="(_, i) in 6"
                :key="i"
                :value="otp[i]"
                @input="handleInput($event, i)"
                @keydown="onKeydown($event, i)"
                @paste="onPaste($event)"
                inputmode="numeric"
                autocomplete="one-time-code"
                pattern="[0-9]*"
                type="tel"
                :disabled="isLoading"
                class="w-11 h-14 sm:w-12 sm:h-14 text-center text-lg font-semibold rounded-xl border border-gray-300 bg-gray-50 text-gray-900 focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15 transition-all disabled:opacity-50"
                :ref="el => setOtpRef(el, i)"
                maxlength="1"
              />
            </div>

            <button
              type="submit"
              :disabled="isLoading || !isComplete"
              class="w-full py-3 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#2a3139] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <template v-if="isLoading">
                <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Activation…</span>
              </template>
              <span v-else>Activer et continuer</span>
            </button>
          </form>
        </template>

        <button
          type="button"
          @click="backToLogin"
          class="mt-5 w-full text-sm text-gray-500 hover:text-[#22282F] transition-colors flex items-center justify-center gap-1"
        >
          <ArrowLeft class="w-4 h-4" />
          Revenir à la connexion
        </button>
      </div>

      <div class="mt-6 text-center">
        <p class="text-xs text-gray-400">v1.0 • © 2025 Ministère des Affaires Étrangères</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ShieldCheck, AlertCircle, ArrowLeft, KeyRound, Copy } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'

const store = useAuthStore()
const router = useRouter()

const qrCode = ref<string | null>(null)
const secret = ref<string | null>(null)
const showSecret = ref(false)
const loadingQr = ref(true)
const isLoading = ref(false)
const error = ref('')

const otp = ref<string[]>(Array(6).fill(''))
const otpRefs = ref<Array<HTMLInputElement | null>>([])
const isComplete = computed(() => otp.value.every((d) => /^\d$/.test(d)))

onMounted(async () => {
  // Accès direct sans flux de login forcé → retour login
  if (!store.setupToken) {
    router.replace('/')
    return
  }
  await loadQr()
})

async function loadQr() {
  loadingQr.value = true
  error.value = ''
  const result = await store.setup2FA()
  loadingQr.value = false
  if (result.success) {
    qrCode.value = result.qrCode || null
    secret.value = result.secret || null
    setTimeout(() => focusIndex(0), 50)
  } else {
    error.value = result.error || 'Impossible de générer le QR code'
  }
}

function setOtpRef(el: any, idx: number) {
  otpRefs.value[idx] = (el as HTMLInputElement) || null
}
function focusIndex(i: number) {
  const el = otpRefs.value[i]
  el?.focus()
  el?.select()
}
function distribute(digits: string) {
  const ds = digits.replace(/\D/g, '').slice(0, 6)
  for (let i = 0; i < 6; i++) otp.value[i] = ds[i] || ''
  const firstEmpty = otp.value.findIndex((d) => !d)
  focusIndex(firstEmpty === -1 ? 5 : firstEmpty)
}
function handleInput(e: Event, idx: number) {
  const target = e.target as HTMLInputElement
  const val = (target.value || '').replace(/\D/g, '')
  if (val.length > 1) { distribute(val); return }
  otp.value[idx] = val
  if (val && idx < 5) focusIndex(idx + 1)
}
function onKeydown(e: KeyboardEvent, idx: number) {
  if (e.key === 'Backspace') {
    if (otp.value[idx]) otp.value[idx] = ''
    else if (idx > 0) { otp.value[idx - 1] = ''; focusIndex(idx - 1) }
    e.preventDefault()
  } else if (e.key === 'ArrowLeft' && idx > 0) { focusIndex(idx - 1); e.preventDefault() }
  else if (e.key === 'ArrowRight' && idx < 5) { focusIndex(idx + 1); e.preventDefault() }
}
function onPaste(e: ClipboardEvent) {
  const digits = (e.clipboardData?.getData('text') || '').replace(/\D/g, '')
  if (!digits) return
  e.preventDefault()
  distribute(digits)
}
function resetCode() {
  otp.value = Array(6).fill('')
  focusIndex(0)
}

async function copySecret() {
  if (secret.value) {
    try { await navigator.clipboard.writeText(secret.value) } catch { /* noop */ }
  }
}

async function regenerateQr() {
  const ok = confirm('Générer un nouveau QR code invalidera l\'ancien. Votre application d\'authentification devra être reconfigurée. Continuer ?')
  if (!ok) return
  otp.value = Array(6).fill('')
  error.value = ''
  loadingQr.value = true
  const result = await store.setup2FA(true)
  loadingQr.value = false
  if (result.success) {
    qrCode.value = result.qrCode || null
    secret.value = result.secret || null
    setTimeout(() => focusIndex(0), 50)
  } else {
    error.value = result.error || 'Impossible de régénérer le QR code'
  }
}

async function confirm() {
  const code = otp.value.join('')
  if (!/^\d{6}$/.test(code)) return
  error.value = ''
  isLoading.value = true
  try {
    const result = await store.confirm2FA(code)
    if (result.status === 'success') {
      router.push('/dashboard')
    } else if (result.status === 'expired') {
      error.value = (result as any).error
      setTimeout(() => router.replace('/'), 1500)
    } else {
      error.value = (result as any).error || 'Code incorrect'
      resetCode()
    }
  } catch {
    error.value = 'Erreur de connexion au serveur'
  } finally {
    isLoading.value = false
  }
}

function backToLogin() {
  router.replace('/')
}
</script>
